import { SplitContainer, useFormContainerContext } from '@if/mui'
import { Stack } from '@mui/material'
import { useGetMerchantMechanicalWarrantyProducts } from '@src/data/api/merchants-api/merchants-api'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { FinancingProgramProvider } from '@src/types/FinancingProgramProvider'
import { MechanicalWarrantyWorksheet } from '@src/types/WorksheetSchema'
import { useEffect } from 'react'
import { useWatch } from 'react-hook-form'
import { useMechanicalWarrantyCompute } from '../hooks/useMechanicalWarrantyCompute'
import MechanicalWarrantyExtended from './MechanicalWarrantyExtended'
import MechanicalWarrantyWorksheetDetails from './MechanicalWarrantyWorksheetDetails'
import MechanicalWarrantyWorksheetLoanPaymentDetails from './MechanicalWarrantyWorksheetLoanPaymentDetails'

interface Props {
  creditApplication: CreditApplication
  merchantMechanicalWarrantyProviders: FinancingProgramProvider[]
  onFundingReadyChange: (value: boolean) => void
}

const MechanicalWarrantyWorksheetBody = ({
  creditApplication,
  merchantMechanicalWarrantyProviders,
  onFundingReadyChange,
}: Props) => {
  const { methods } = useFormContainerContext<MechanicalWarrantyWorksheet>()
  const { control } = methods

  const selectedProviderId = useWatch({ control, name: 'providerId' })
  const selectedProvider = merchantMechanicalWarrantyProviders?.find((p) => p.id === selectedProviderId) ?? null

  const [merchantMechanicalWarrantyProducts, isLoadingProducts] = useGetMerchantMechanicalWarrantyProducts(
    creditApplication?.merchantId ?? '',
    selectedProviderId ?? '',
  )

  const worksheet = useWatch({ control }) as MechanicalWarrantyWorksheet

  const { computeDto, computedResult, isComputing } = useMechanicalWarrantyCompute({
    creditApplicationId: creditApplication.id,
    worksheet,
    merchantMechanicalWarrantyProvider: selectedProvider,
  })

  // Surface funding readiness: block submission while a compute is in flight or the inputs are incomplete,
  // so a stale computed amount can never be persisted.
  useEffect(() => {
    onFundingReadyChange(!isComputing && computeDto != null)
  }, [isComputing, computeDto, onFundingReadyChange])

  return (
    <SplitContainer
      main={
        <Stack spacing={3}>
          <MechanicalWarrantyExtended
            merchantMechanicalWarrantyProviders={merchantMechanicalWarrantyProviders}
            merchantMechanicalWarrantyProducts={merchantMechanicalWarrantyProducts ?? []}
            isLoadingProducts={isLoadingProducts}
          />
          <MechanicalWarrantyWorksheetDetails
            creditApplication={creditApplication}
            computedLoanAmounts={computedResult}
            merchantMechanicalWarrantyProducts={merchantMechanicalWarrantyProducts ?? []}
            isComputing={isComputing}
          />
        </Stack>
      }
      aside={
        <MechanicalWarrantyWorksheetLoanPaymentDetails
          computedLoanAmounts={computeDto ? computedResult : null}
          isComputing={isComputing}
        />
      }
    />
  )
}

export default MechanicalWarrantyWorksheetBody
